/* eslint-disable no-unused-vars */
/*global SunergoPlatform, A11yDialog */
import { createPopper } from '@popperjs/core';
import _ from 'lodash';

// Sets _csrf to the header of every AJAX request (in general, not only jQuery)
// global csrf only
(function() {
	var tokenValue = document.querySelector('meta[name="csrfToken"]');
	if (!tokenValue) return;

	var send = XMLHttpRequest.prototype.send,
		token = tokenValue.getAttribute('content');
	XMLHttpRequest.prototype.send = function(data) {
		this.setRequestHeader('csrf-token', token);
		return send.apply(this, arguments);
	};
})();

// Utilities
function initUtilities() {
	SunergoPlatform.loading = {
		items: [],
		add: function(id) {
			const index = this.items.indexOf(id);
			if (index < 0) this.items.push(id);
			document.body.classList.remove('is-loaded');
		},
		remove: function(id) {
			const index = this.items.indexOf(id);
			if (index > -1) this.items.splice(index, 1);
			else console.warn('[Sunergo Platform] Could not remove loading item because it was not added: ', id);

			if (this.items.length < 1) document.body.classList.add('is-loaded');
		}
	};
	SunergoPlatform.loading.add('global-setup');

	SunergoPlatform.datetimeFormatter = {
		locales: {
			am: 'a.m.',
			pm: 'p.m.',
			months: {
				'1': 'Jan',
				'2': 'Feb',
				'3': 'Mar',
				'4': 'Apr',
				'5': 'May',
				'6': 'Jun',
				'7': 'Jul',
				'8': 'Aug',
				'9': 'Sep',
				'10': 'Oct',
				'11': 'Nov',
				'12': 'Dec'
			}
		},
		timeObject(time) {
			const timeStringRegex = /^([01]?\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/;
			time = time.toString().match(timeStringRegex) || [time];

			if (time.length <= 1) return time;
			time = time[0].split(':');
			const hour12 = parseInt(+time[0]) % 12 || 12;
			const ampm = parseInt(time[0]) < 12 ? 'am' : 'pm';
			return {
				hour: time[0],
				hour12: hour12.toString(),
				minute: time[1],
				second: time[2],
				ampm
			};
		},
		timeDuration(startTime, endTime, nextDay) {
			const start = this.timeObject(startTime);
			const end = this.timeObject(endTime);

			let hours = parseInt(end.hour) - parseInt(start.hour);
			let minutes = parseInt(end.minute) - parseInt(start.minute);

			if (nextDay || hours < 0) {
				nextDay = true;
				hours += 24;
			}

			return {
				hours,
				minutes,
				totalHours: hours + minutes / 60,
				totalMinutes: hours * 60 + minutes,
				nextDay
			};
		},
		timeAdd(time, duration) {
			let hours = parseInt(time.hour);
			let minutes = parseInt(time.minute) + parseInt(duration);

			// round minutes into hours
			while (minutes > 59) {
				minutes = minutes - 60;
				hours++;
			}

			// round hours into days
			let days = 0;
			while (hours > 23) {
				hours = hours - 24;
				days++;
			}

			// convert to timeObject
			hours = hours.toString();
			if (hours.length < 2) hours = '0' + hours;
			minutes = minutes.toString();
			if (minutes.length < 2) minutes = '0' + minutes;

			const to = this.timeObject(`${hours}:${minutes}`);

			return { to, days };
		},
		timeDisplay12(time) {
			if (!time) return '';
			const tObject = typeof time == 'object' ? time : this.timeObject(time);
			if (typeof tObject !== 'object') return time;
			return `${tObject.hour12}:${tObject.minute} ${this.locales[tObject.ampm]}`;
		},
		dateObject(date) {
			const dateStringRegex = /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])$/;
			date = date.toString().match(dateStringRegex) || [date];

			if (date.length > 1) {
				date = date[0].split('-');
				for (let i = 0; i < date.length; i++) {
					date[i] = parseInt(date[i]);
				}
				return { year: date[0], month: date[1], day: date[2] };
			} else return date;
		},
		dateDisplayLong(date) {
			if (!date) return '';
			const dObject = this.dateObject(date);
			if (typeof dObject == 'object')
				return `${this.locales.months[dObject.month]} ${dObject.day}, ${
					dObject.year
				}`;
			else return date;
		},
		dateTimeDisplay(dateTime) {
			if (!dateTime) return '';
			dateTime = dateTime.split(' ');
			return `${this.dateDisplayLong(dateTime[0])} ${this.timeDisplay12(dateTime[1])}`;
		}
	};
}

// JS Templates
function initTemplates() {
	SunergoPlatform.templates = {
		items: {},
		init() {
			if (this.items.length > 0) return console.warn('Templates already initialized');
			const elements = document.querySelectorAll('template[data-template]');
			for (let i = 0; i < elements.length; i++) {
				const item = elements[i];
				if (!item.content) continue;
				this.items[item.dataset.template || item.id] = item.content.firstElementChild;
			}
		},
		clone(name) {
			const element = this.items[name];
			if (element) return element.cloneNode(true);
			else return console.error(`Could not find template: ${name}`);
		}
	};

	// get all templates and assign them with a name
	SunergoPlatform.templates.init();
}

// Feedback messages
function initFeedback() {
	SunergoPlatform.feedbackBanner = {
		area: document.querySelector('[data-app-feedback]'),
		show(message) {
			if (!message.id) return console.warn('[Feedback Banner] Missing ID.', message);
			if (!message.type) message.type = 'info';

			// if message already in list, scroll to banner and flash
			const existingMessage = this.area.querySelector(`[data-message-id='${message.id}']`);
			if (existingMessage && !existingMessage.dataset.feedbackHiding) {
				this.area.scrollIntoView(true);
				existingMessage.dataset.feedbackFlashing = true;
				setTimeout(function() {
					existingMessage.dataset.feedbackFlashing = false;
				}, 600);
				return;
			}

			// clone feedback message
			const bannerContainer = SunergoPlatform.templates.clone('feedback');

			if (!bannerContainer)
				return console.error('Feedback Banner could not be cloned.', message);
			const banner = bannerContainer.firstChild;

			// clone type icon
			const typeIcon = SunergoPlatform.templates.clone(`status-${message.type}`);

			// set the message type
			banner.classList.add(`feedback--${message.type}`);
			banner.getElementsByClassName('feedback__icon')[0].appendChild(typeIcon);

			let bannerTitle = banner.getElementsByClassName('feedback__title')[0],
				bannerContent = banner.getElementsByClassName('feedback__content')[0],
				bannerActions = banner.getElementsByClassName('feedback__actions')[0];

			// insert text
			if (message.title) bannerTitle.innerHTML = message.title;

			if (message.content) bannerContent.innerHTML = message.content;

			if (!message.actions) bannerActions.innerHTML = '';
			else {
				const button = bannerActions.getElementsByClassName('button')[0];

				button.getElementsByClassName('button__label')[0].innerHTML = message.actions.label;

				if (message.actions.attributes) {
					for (let attr in message.actions.attributes) {
						button.setAttribute(attr, message.actions.attributes[attr]);
					}
				}

				if (message.actions.href) {
					button.href = message.actions.href;
					button.target = message.actions.target;
				} else if (message.actions.action) {
					const actionButton = document.createElement('button');
					actionButton.dataset.action = message.actions.action;
					for (const attr of button.attributes) {
						if (attr.name == 'target' || attr.name == 'href') continue;
						actionButton.setAttribute(attr.name, attr.value);
					}
					actionButton.innerHTML = button.innerHTML;
					button.replaceWith(actionButton);
				}
			}

			// add in the message id for targeted removal
			if (message.id) bannerContainer.dataset.messageId = message.id;

			// insert the banner and animate it in
			this.area.appendChild(bannerContainer);

			// Initialize Action Buttons that are inside the banner.
			bannerContainer.querySelectorAll('[data-action]').forEach(function(button) {
				SunergoPlatform.actionButtons.initButton(button);
			});

			// scroll to banner
			this.area.scrollIntoView(true);
		},
		clear(id) {
			if (id) {
				const item = this.area.querySelector(`[data-message-id="${id}"]`);
				if (item) this.clearItem(item);
			} else {
				if (this.area.children.length < 1) return;
				for (let i = 0; i < this.area.children.length; i++) {
					this.clearItem(this.area.children[i]);
				}
			}
		},
		clearItem(item) {
			item.dataset.feedbackHiding = true;
			setTimeout(function() {
				item.remove();
			}, 300);
		}
	};

	const banners = SunergoPlatform.feedbackBanner.area;
	if (!banners || banners.length <= 0) return;

	const expires = banners.querySelectorAll('[data-feedback-expires]');
	if (!expires || expires.length <= 0) return;
	expires.forEach(function(item) {
		const delay = item.dataset.feedbackExpires * 1000;
		setTimeout(function() {
			if (!item) return;
			item.parentElement.dataset.feedbackHiding = true;
			setTimeout(function() {
				if (!item) return;
				item.parentElement.remove();
			}, 300);
		}, delay);
	});
}

function initPopoverMenus() {
	SunergoPlatform.popoverMenus = {
		initAll() {
			const menus = document.querySelectorAll('[data-popover-menu]');
			menus.forEach(item => {
				if (item.dataset.popoverMenu != 'ready') SunergoPlatform.popoverMenus.init(item);
			});
		},
		init(element) {
			if (element.dataset.dropdownMenu == 'ready')
				return console.warn('Popover Menu already initialized', element);
			const trigger = element.querySelector('[aria-haspopup="true"]');
			const popoverMenu = element.querySelector('[role="menu"]');

			if (!trigger || !popoverMenu) return;

			// Setup Popper instance and don't move popover until expanded
			const popperInstance = createPopper(trigger, popoverMenu, {
				modifiers: [
					{
						name: 'eventListeners',
						enabled: false
					}
				]
			});

			// Setup toggle function
			const togglePopover = function(expand) {
				trigger.setAttribute('aria-expanded', expand);
				if (expand) {
					popoverMenu.hidden = false;
					popoverMenu.dataset.closing = false;
				} else if (window.matchMedia('(prefers-reduced-motion)').matches) {
					popoverMenu.hidden = true;
					popoverMenu.dataset.closing = false;
				} else {
					popoverMenu.dataset.closing = true;
					setTimeout(() => {
						if (popoverMenu.dataset.closing) popoverMenu.hidden = true;
						popoverMenu.dataset.closing = false;
					}, 50);
				}

				if (!popperInstance) return;
				popperInstance.setOptions({
					modifiers: [
						{
							name: 'eventListeners',
							enabled: expand
						},
						{
							name: 'offset',
							options: {
								offset: [0, 4]
							}
						},
						{
							name: 'preventOverflow',
							options: {
								padding: 8
							}
						}
					]
				});
			};

			// Setup closing popover function
			const outsideClicked = event => {
				if (element.contains(event.target)) return false;
				togglePopover(false);
				document.removeEventListener('click', outsideClicked);
			};

			// Open popover when trigger clicked
			const triggerClicked = event => {
				event.preventDefault();

				// Open/close popover based on current state
				const isExpanded = trigger.getAttribute('aria-expanded') === 'true' || false;
				togglePopover(!isExpanded);

				// Add listener to close popover if clicked outside
				document.addEventListener('click', outsideClicked);
			};

			// Add listener for when trigger is clicked
			trigger.addEventListener('click', triggerClicked);

			element.dataset.popoverMenu = 'ready';
		}
	};
}

function initActionButtons() {
	SunergoPlatform.actionButtons = {
		actions: [],
		buttons: [],
		initDisableOnActive(button) {
			// Check if button is already setup and "ready"
			if (button.dataset.disableOnActive == 'ready') return;
			var _this = this;
			// Add a click listener to the button
			button.addEventListener('click', function(e) {
				if (_this.isActive(button)) e.preventDefault();
				else _this.setActive(button, true);
			});

			// Set the button to "ready"
			button.dataset.disableOnActive = 'ready';
		},
		setActive(button, state) {
			button.dataset.isActive = state ? true : '';
			if (state) button.setAttribute('aria-disabled', true);
			else button.removeAttribute('aria-disabled');
			const feedback = button.querySelector('[data-button-feedback]');
			if (feedback) feedback.innerHTML = state ? `(${feedback.dataset.buttonFeedback})` : '';
		},
		setActiveAll(state) {
			this.buttons.forEach(button => {
				this.setActive(button, state);
			});
		},
		disableAll(state) {
			this.buttons.forEach(button => {
				if (state) button.setAttribute('aria-disabled', true);
				else button.removeAttribute('aria-disabled');
			});
		},
		isActive(button) {
			return button.dataset.isActive || false;
		},
		resetAll() {
			const activeButtons = document.querySelectorAll('[data-is-active="true"]');

			activeButtons.forEach(button => {
				this.setActive(button, false);
			});
		},
		addAction(action) {
			this.actions.push(action);
		},
		initButton(button) {
			this.buttons.push(button);

			// Skip setup for form and generic action buttons.
			if (['submit', 'reset', 'action', 'content-nav-toggle'].includes(button.dataset.action)) return;

			// If a tooltip is present, add a listener to show/hide the tooltip
			if (button.dataset.hasTooltip) {
				const tooltipEl = button.nextElementSibling;
				if (tooltipEl && tooltipEl.dataset.tooltip) {
					button.tooltip = {
						element: tooltipEl,
						show({ timeout = false }) {
							this.element.setAttribute('data-show', '');
							this.popper.update();

							if (timeout && timeout > 0) {
								setTimeout(() => {
									this.hide();
								}, timeout);
							}
						},
						hide() {
							this.element.removeAttribute('data-show');
						}
					};

					button.tooltip.element = tooltipEl;
					button.tooltip.popper = createPopper(button, tooltipEl);
				}
			}

			// Add a listener to action buttons.
			button.addEventListener('click', function(e) {
				// Switch state if this button is switchable.
				changeSwitchState(button);

				// Run all actions and stop when an action is run.
				const action = button.dataset.action;
				for (let i = 0; i < SunergoPlatform.actionButtons.actions.length; i++) {
					if (SunergoPlatform.actionButtons.actions[i](action, button, e)) break;
				}
			});
		}
	};

	// Disable Submit Button when processing
	let disableOnActiveButtons = document.querySelectorAll('[data-disable-on-active]');

	disableOnActiveButtons.forEach(button => {
		SunergoPlatform.actionButtons.initDisableOnActive(button);
	});

	// Button switches state on press.
	const changeSwitchState = function(button) {
		if (button.getAttribute('role') != 'switch') return;
		if (button.getAttribute('aria-checked') == 'true') {
			button.setAttribute('aria-checked', 'false');
			button.setAttribute('aria-label', button.dataset.switchLabelOff);
		} else {
			button.setAttribute('aria-checked', 'true');
			button.setAttribute('aria-label', button.dataset.switchLabelOn);
		}
	};

	// Reset all buttons to their original state.
	const resetAllActiveButtons = function(action) {
		if (action != 'reset-all-active-buttons') return false;
		SunergoPlatform.actionButtons.resetAll();
		return true;
	};

	// Copy text given in "textToCopy" to clipboard.
	const copyToClipboard = function(action, button) {
		if (action != 'copy-to-clipboard') return false;
		let textToCopy = '';

		if (button.dataset.copyInput) {
			// copy from input
			const inputToCopy = document.querySelector(button.dataset.copyInput);
			if (inputToCopy) textToCopy = inputToCopy.value.toString();
		} else if (button.dataset.copyText) {
			textToCopy = button.dataset.copyText.toString();
		}

		try {
			// Copy text using Clipboard API
			navigator.clipboard.writeText(textToCopy).then(
				function() {
					// success
				},
				function() {
					// failure
					console.error('Failed to copy text: ', textToCopy);
				}
			);
		} catch (e) {
			// If no permissions or Clipboard API is not supported, use execCommand.
			var textArea = document.createElement('textarea');
			textArea.value = textToCopy;
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand('copy');
			document.body.removeChild(textArea);
			console.info('Text copied using execCommand: ', textToCopy);
		}
		button.tooltip.show({ timeout: 3000 });

		return true;
	};

	// Navigator share()
	const share = function(action, button) {
		if (action != 'share') return false;

		const shareData = {
			title: button.dataset.shareTitle,
			text: button.dataset.shareText,
			url: button.dataset.shareUrl
		};

		if (!navigator.share) {
			// Copy to clipboard.
			button.dataset.copyText = shareData.url;
			copyToClipboard('copy-to-clipboard', button);
			return true;
		}

		navigator.share(shareData).then(() => {
		}).catch((error) => {
			button.dataset.copyText = shareData.url;
			copyToClipboard('copy-to-clipboard', button);
		});

		return true;
	};

	// Print page.
	const printPage = function(action) {
		if (action != 'print-page') return false;
		window.print();
		return true;
	};

	// Open a new window.
	const openWindow = function(action, button) {
		if (action != 'open-window') return false;
		window.open(button.dataset.href);
		return true;
	};

	// Close window.
	const closeWindow = function(action) {
		if (action != 'close-window') return false;
		window.close();
		return true;
	};

	// Clear feedback.
	const clearFeedback = function(action, button) {
		if (action != 'close-feedback' || !SunergoPlatform.feedbackBanner) return false;
		SunergoPlatform.feedbackBanner.clear(button.dataset.closeMessageId);
		return true;
	};

	SunergoPlatform.actionButtons.actions.push(resetAllActiveButtons, copyToClipboard, share, printPage, openWindow, closeWindow, clearFeedback);

	const buttons = document.querySelectorAll('[data-action]');

	buttons.forEach(button => {
		SunergoPlatform.actionButtons.initButton(button);
	});
}

const initChangeHistory = function() {
	SunergoPlatform.changeHistory = {
		queries: {},
		change: function(query, value, push) {
			this.queries[query] = value;
			if (!history.replaceState) return;

			const searchParams = new URLSearchParams();

			for (let query in this.queries) {
				const value = this.queries[query];
				if (!value || value == '') continue;
				searchParams.set(query, value);
			}

			let queryString = searchParams.toString();
			let url = queryString != '' ? `?${queryString}` : window.location.pathname;
			if (window.location.hash) url += window.location.hash;

			if (push) history.pushState({}, '', url);
			else history.replaceState({}, '', url);
		},
	};

	// Get initial queries.
	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	for (let query of urlParams.entries()) {
		SunergoPlatform.changeHistory.queries[query[0]] = query[1];	
	}
};

// Run scripts on document loaded.
SunergoPlatform.onLoad.push(
	initUtilities,
	initTemplates,
	initFeedback,
	initPopoverMenus,
	initActionButtons,
	initChangeHistory
);
